import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaPaperclip, FaTrash } from 'react-icons/fa';
import './Violation.css';
import AttachmentsPopup from './AttachmentsPopup';

const VIOLATION_OPTIONS = [
  'Mobile phone use',
  'Not wearing Seat Belt',
  'Eating/Drinking while driving',
  'Smoking inside cabin',
  'Not wearing coverall',
  'Distracted while driving',
  'Unsafe posture while driving',
  'Fatigued while driving',
  'Overspeeding',
  'Harsh Breaking',
  'Harsh Acceleration',
  'Driving too close to other vehicles',
  'Night Driving',
  'Rest violation',
  'Not following JP',
  'Tampering the devices',
  'Unauthorized Passenger',
  'Other'
];

const AddViolation = () => {
  const [formData, setFormData] = useState({
    journey_plane_no: '',
    scheduled_vehicle: '',
    driver_name: '',
    driver_phone: '',
    driver_id: '',
    carrier: '',
    violation: VIOLATION_OPTIONS[0],
    remarks: '',
    violation_time: new Date().toISOString().slice(0, 16),
    jp_status: 'IN_TRANSIT',
    ivms_point: ''
  });
  const [trucks, setTrucks] = useState([]);
  const [message, setMessage] = useState('');
  const [violationId, setViolationId] = useState(null);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const isSubmitted = !!violationId;

  const getCurrentLocalDateTime = () => {
    const localDate = new Date();
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    fetchInTransitTrucks();
    setFormData(prev => ({ ...prev, violation_time: getCurrentLocalDateTime() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInTransitTrucks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/inTransitTrucks`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrucks(response.data || []);
    } catch (err) {
      console.error('Error fetching in-transit trucks:', err);
    }
  };

  const handleTruckChange = (e) => {
    const { name, value } = e.target;
    const selectedTruck = trucks.find(
      (truck) => truck.journey_plane_no === value || truck.scheduled_vehicle === value
    );

    if (selectedTruck) {
      setFormData((prev) => ({
        ...prev,
        journey_plane_no: selectedTruck.journey_plane_no,
        scheduled_vehicle: selectedTruck.scheduled_vehicle,
        driver_name: selectedTruck.driver_name,
        driver_phone: selectedTruck.driver_phone || '',
        carrier: selectedTruck.carrier,
        ivms_point: selectedTruck.ivms_point || '',
        jp_status: selectedTruck.jp_status || 'IN_TRANSIT',
      }));
    } else {
      // allow manual entry: only update the field user changed, clear dependent fields
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(name === 'journey_plane_no' && { scheduled_vehicle: '' }),
        ...(name === 'scheduled_vehicle' && { journey_plane_no: '' }),
        driver_name: '',
        driver_phone: '',
        carrier: '',
        ivms_point: '',
        jp_status: 'IN_TRANSIT',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const convertToUTC = (localDateTime) => {
    const date = new Date(localDateTime);
    if (isNaN(date.getTime())) {
      console.error('Invalid date value:', localDateTime);
      return null;
    }
    return date.toISOString();
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmitted || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const dataToSubmit = {
        ...formData,
        violation_time: convertToUTC(formData.violation_time),
        violation_status: 'OPEN'
      };

      Object.keys(dataToSubmit).forEach(key => {
        if ((dataToSubmit[key] === undefined || dataToSubmit[key] === '') && key !== 'driver_phone' && key !== 'remarks') {
          dataToSubmit[key] = null;
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/driver-violations`,
        dataToSubmit,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setViolationId(response.data?.violationId || null);
      setMessage('Violation recorded successfully! You can now attach files below.');
      // Keep message briefly visible
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error adding violation:', error);
      setMessage('Failed to record violation. Please try again.');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenAttachments = () => {
    setMessage('Form already submitted — only upload attachments and close this window.');
    setShowAttachments(true);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleDeleteViolation = async () => {
    if (!violationId) return;
    if (!window.confirm('Delete this violation? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/${violationId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMessage('Violation deleted');
      setTimeout(() => setMessage(''), 3000);
      navigate('/driver-violations');
    } catch (err) {
      console.error('Error deleting violation:', err);
      setMessage('Failed to delete violation');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div className="container-add-violation">
      <button className="close-button" onClick={() => navigate('/driver-violations')}>×</button>
      <h2 className="violation-form-header">🚨 Add Driving Violation</h2>

      {message && (
        <div className={`violation-message ${message.includes('successfully') ? 'success' : 'error'}`}>
          {message}
          {violationId && message.includes('successfully') && (
            <span style={{ display: 'block', marginTop: '8px', fontSize: '0.9em', fontWeight: 'bold' }}>
              🔗 Violation ID: #{violationId}
            </span>
          )}
        </div>
      )}

      <div className="violation-form-wrapper">
        <form onSubmit={handleSubmit}>
          <div className="violation-form-row">
            <div className="violation-form-group">
              <label>Journey / JP No *</label>
              <input
                list="journey-list"
                name="journey_plane_no"
                value={formData.journey_plane_no}
                onChange={handleTruckChange}
                placeholder="Type or select journey..."
                required
                disabled={isSubmitted || isSubmitting}
              />
              <datalist id="journey-list">
                {trucks.map((truck) => (
                  <option key={`j-${truck.journey_plane_no}`} value={truck.journey_plane_no} />
                ))}
              </datalist>
            </div>
            <div className="violation-form-group">
              <label>Vehicle *</label>
              <input
                list="vehicle-list"
                name="scheduled_vehicle"
                value={formData.scheduled_vehicle}
                onChange={handleTruckChange}
                placeholder="Type or select vehicle..."
                required
                disabled={isSubmitted || isSubmitting}
              />
              <datalist id="vehicle-list">
                {trucks.map((truck) => (
                  <option key={`v-${truck.scheduled_vehicle}`} value={truck.scheduled_vehicle} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="violation-form-row">
            <div className="violation-form-group">
              <label>Driver Name</label>
              <input
                type="text"
                name="driver_name"
                value={formData.driver_name}
                onChange={handleChange}
                placeholder="Auto-filled from truck selection or enter manually"
                disabled={isSubmitted || isSubmitting}
              />
            </div>
            <div className="violation-form-group">
              <label>Driver Phone</label>
              <input
                type="text"
                name="driver_phone"
                value={formData.driver_phone}
                onChange={handleChange}
                placeholder="Auto-filled from truck selection or enter manually"
                disabled={isSubmitted || isSubmitting}
              />
            </div>
          </div>

          <div className="violation-form-row">
            <div className="violation-form-group">
              <label>Carrier</label>
              <input
                type="text"
                name="carrier"
                value={formData.carrier}
                onChange={handleChange}
                placeholder="Auto-filled from truck selection or enter manually"
                disabled={isSubmitted}
              />
            </div>
            <div className="violation-form-group">
              <label>IVMS Point</label>
              <input
                type="text"
                name="ivms_point"
                value={formData.ivms_point}
                onChange={handleChange}
                placeholder="Auto-filled from truck selection or enter manually"
                disabled={isSubmitted}
              />
            </div>
          </div>

          <div className="violation-form-row full">
            <div className="violation-form-group">
              <label>Violation Type *</label>
              <select
                name="violation"
                value={formData.violation}
                onChange={handleChange}
                required
                disabled={isSubmitted || isSubmitting}
              >
                {VIOLATION_OPTIONS.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="violation-form-row">
            <div className="violation-form-group">
              <label>Violation Time *</label>
              <input
                type="datetime-local"
                name="violation_time"
                value={formData.violation_time}
                onChange={handleChange}
                required
                disabled={isSubmitted || isSubmitting}
              />
            </div>
            <div className="violation-form-group">
              <label>JP Status</label>
              <input
                type="text"
                name="jp_status"
                value={formData.jp_status}
                onChange={handleChange}
                placeholder="Auto-filled from truck selection or enter manually"
                disabled={isSubmitted || isSubmitting}
              />
            </div>
          </div>

          <div className="violation-form-row full">
            <div className="violation-form-group">
              <label>Remarks / Notes</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Add any additional remarks or details about the violation..."
                disabled={isSubmitted || isSubmitting}
              />
            </div>
          </div>

          <div className="violation-form-actions">
            <button
              type="button"
              className={`submit ${isSubmitted || isSubmitting ? 'disabled' : ''}`}
              onClick={() => {
                if (isSubmitted) {
                  setMessage('Form already submitted — only upload attachments and close this window.');
                  setTimeout(() => setMessage(''), 5000);
                } else if (!isSubmitting) {
                  handleSubmit();
                }
              }}
              disabled={isSubmitted || isSubmitting}
            >
              {isSubmitted ? 'Submitted' : isSubmitting ? 'Saving...' : 'Save Violation'}
            </button>
            <button type="button" className="cancel" onClick={() => navigate('/driver-violations')}>
              Cancel
            </button>
            {violationId && (
              <button
                type="button"
                title="Attach files to this violation"
                className="attachment-icon-btn"
                onClick={handleOpenAttachments}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#0066cc',
                  marginLeft: '8px',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e8f0ff';
                  e.currentTarget.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FaPaperclip />
              </button>
            )}
            {violationId && (
              <button
                type="button"
                title="Delete this violation"
                className="attachment-icon-btn"
                onClick={handleDeleteViolation}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#c92b2b',
                  marginLeft: '8px',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#ffecec';
                  e.currentTarget.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FaTrash />
              </button>
            )}
          </div>
        </form>
      </div>
      {showAttachments && violationId && (
        <AttachmentsPopup violationId={violationId} onClose={() => setShowAttachments(false)} />
      )}
    </div>
  );
};

export default AddViolation;
